import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { USERS_TAG_TYPE } from './constants';

interface NotificationSettings {
  [key: string]: {
    value: 'ENABLED' | 'DISABLED';
  };
}

interface ManagedUsersResponse {
  results: MedScout.User[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [USERS_TAG_TYPE],
  endpoints: (builder) => ({
    // Get current user (refreshes user data from API)
    getCurrentUser: builder.query<MedScout.User, void>({
      query: () => ({
        url: 'v1/auth/me/',
        method: 'GET',
      }),
      transformResponse: (response: { result: MedScout.User }) =>
        response.result,
      providesTags: [{ type: USERS_TAG_TYPE, id: 'CURRENT_USER' }],
    }),

    // Get user notification settings
    getUserNotificationSettings: builder.query<NotificationSettings, void>({
      query: () => ({
        url: 'v1/settings/notifications',
        method: 'GET',
      }),
      providesTags: [{ type: USERS_TAG_TYPE, id: 'NOTIFICATION_SETTINGS' }],
    }),

    // Update user notification settings with optimistic updates
    updateUserNotificationSettings: builder.mutation<
      NotificationSettings,
      { notification_settings: NotificationSettings }
    >({
      query: ({ notification_settings }) => ({
        url: 'v1/settings/notifications',
        method: 'PUT',
        body: { notification_settings },
      }),
      // Optimistic update
      async onQueryStarted(
        { notification_settings },
        { dispatch, queryFulfilled }
      ) {
        // Optimistically update the cache
        const patchResult = dispatch(
          usersApi.util.updateQueryData(
            'getUserNotificationSettings',
            undefined,
            (draft) => {
              Object.assign(draft, notification_settings);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          // Revert the optimistic update on error
          patchResult.undo();
        }
      },
      invalidatesTags: [{ type: USERS_TAG_TYPE, id: 'NOTIFICATION_SETTINGS' }],
    }),

    // Get managed users
    getManagedUsers: builder.query<
      ManagedUsersResponse,
      { companyId: string; userId: string; queryParams?: string }
    >({
      query: ({ companyId, userId, queryParams = '' }) => ({
        url: `v1/accounts/companies/${companyId}/users/${userId}/managed_users/${queryParams}`,
        method: 'GET',
      }),
      providesTags: (result, error, { userId }) => [
        { type: USERS_TAG_TYPE, id: `MANAGED_USERS_${userId}` },
      ],
    }),

    // Start user impersonation
    // Note: No invalidatesTags - we do a full page reload after impersonation
    // which fetches fresh user data. Invalidating here causes a race condition
    // where RTK Query refetches and triggers re-renders before the reload,
    // leading to errors in some browsers (Firefox/Safari).
    startImpersonation: builder.mutation<MedScout.User, { userId: number }>({
      query: ({ userId }) => ({
        url: '/v1/admin/impersonation/',
        method: 'POST',
        body: { impersonate: userId },
      }),
    }),

    // Stop user impersonation
    // Note: No invalidatesTags - same reason as startImpersonation above.
    stopImpersonation: builder.mutation<void, void>({
      query: () => ({
        url: '/v1/admin/impersonation/',
        method: 'DELETE',
        body: {},
      }),
    }),

    // Unsubscribe from notification emails. Anonymous by design: the signed
    // token from the email link is the credential, so no auth is required.
    unsubscribe: builder.mutation<void, { token: string }>({
      query: ({ token }) => ({
        url: 'v1/settings/notifications/unsubscribe',
        method: 'POST',
        body: { token },
      }),
      invalidatesTags: [{ type: USERS_TAG_TYPE, id: 'NOTIFICATION_SETTINGS' }],
    }),

    // Accept terms
    acceptTerms: builder.mutation<void, { termsVersion: string }>({
      query: ({ termsVersion }) => ({
        url: 'v1/terms/accept',
        method: 'PUT',
        body: {
          terms_version: termsVersion,
        },
      }),
      invalidatesTags: [{ type: USERS_TAG_TYPE, id: 'CURRENT_USER' }],
    }),

    // Update current user
    updateCurrentUser: builder.mutation<
      MedScout.User,
      { last_territory_id: number | null }
    >({
      query: (data) => ({
        url: 'v1/account/current-user/',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: USERS_TAG_TYPE, id: 'CURRENT_USER' }],
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useGetUserNotificationSettingsQuery,
  useUpdateUserNotificationSettingsMutation,
  useGetManagedUsersQuery,
  useStartImpersonationMutation,
  useStopImpersonationMutation,
  useUnsubscribeMutation,
  useAcceptTermsMutation,
  useUpdateCurrentUserMutation,
} = usersApi;
